import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, ChevronDown, Menu, Sun, Moon, LogOut, User as UserIcon, Plane, Ship, X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useTheme } from '../../context/ThemeContext';
import { getCloudFrontUrl } from '../../utils/s3Upload';

const chip = 'inline-flex items-center justify-center rounded-xl border transition-colors';
const quiet = 'border-slate-200 bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-50 ' +
              'dark:border-slate-700/70 dark:bg-slate-800/60 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-700/60';

const ROLE_LABEL = { super_admin: 'Super Admin', admin: 'Admin', accountant: 'Accountant', employee: 'Employee' };

/** Search, environment, notifications and the account menu, above an admin console. */
const AdminTopbar = ({ search = '', onSearch, placeholder = 'Search user, AWB, HSC, wallet…', onOpenMenu }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { notifications = [], unreadCount = 0, markAllRead } = useNotifications();
  const { isDarkMode, toggleTheme } = useTheme();

  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarBroke, setAvatarBroke] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const menuRef = useRef(null);
  const bellRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // ⌘K / Ctrl+K puts the caret in the search box, as the hint promises.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="sticky top-0 z-20 flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3
                       bg-white/90 dark:bg-[#0b1220]/90 backdrop-blur
                       border-b border-slate-200/80 dark:border-slate-700/60">
      <button type="button" onClick={onOpenMenu}
        className={`${chip} ${quiet} lg:hidden w-10 h-10`} aria-label="Open menu">
        <Menu size={18} />
      </button>

      {onSearch ? (
      <div className="relative flex-1 max-w-[520px]">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={searchRef}
          value={search}
          onChange={(e) => onSearch?.(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full h-11 pl-10 pr-14 rounded-xl text-sm
                     bg-slate-50 dark:bg-slate-800/70
                     border border-slate-200 dark:border-slate-700/70
                     text-slate-800 dark:text-slate-100 placeholder-slate-400
                     outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition"
        />
        <kbd className="hidden sm:flex absolute right-2.5 top-1/2 -translate-y-1/2 items-center gap-0.5
                        px-1.5 h-6 rounded-md text-[11px] font-semibold
                        bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                        text-slate-400 pointer-events-none">⌘ K</kbd>
        {search && (
          <button type="button" onClick={() => onSearch('')} aria-label="Clear search"
            className="absolute right-14 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400
                       hover:text-slate-600 dark:hover:text-slate-200">
            <X size={14} />
          </button>
        )}
      </div>
      ) : <div className="flex-1" />}

      <div className="ml-auto flex items-center gap-2 sm:gap-2.5">
        <button type="button" onClick={toggleTheme}
          className={`${chip} ${quiet} w-10 h-10`} aria-label="Toggle theme">
          {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="relative" ref={bellRef}>
          <button type="button" onClick={() => { setBellOpen((v) => !v); setMenuOpen(false); }}
            className={`${chip} ${quiet} w-10 h-10 relative`} aria-label="Notifications">
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center
                               bg-red-500 text-white text-[10px] font-black">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {bellOpen && (
            <div className="absolute right-0 mt-2 w-[360px] max-h-[420px] overflow-y-auto rounded-2xl shadow-2xl z-30
                            bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <p className="text-sm font-bold text-slate-900 dark:text-white">Notifications</p>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs font-bold text-blue-600 dark:text-blue-400">
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-400">Nothing new.</p>
              ) : notifications.slice(0, 20).map((n, i) => {
                const data = n.data || n;
                const isSea = String(data.type || '').toLowerCase().includes('sea');
                const id = isSea ? data.blNumber : (data.awbNo || data.awbNumber);
                return (
                  <div key={n.id || i} className="px-4 py-3 border-b border-slate-50 dark:border-slate-800/70 last:border-0">
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 p-2 rounded-xl flex-shrink-0 ${isSea
                        ? 'bg-cyan-100 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-300'
                        : 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300'}`}>
                        {isSea ? <Ship size={16} /> : <Plane size={16} />}
                      </span>
                      <div className="min-w-0">
                        <p className="text-[13px] font-bold text-slate-900 dark:text-white">
                          New {isSea ? 'Sea' : 'Air'} Shipment
                        </p>
                        <p className="text-[11.5px] text-slate-500 dark:text-slate-400 truncate">{id || '—'}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="relative" ref={menuRef}>
          <button type="button" onClick={() => { setMenuOpen((v) => !v); setBellOpen(false); }}
            className="flex items-center gap-2.5 h-10 pl-1 pr-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition">
            {user?.profilePicture && !avatarBroke ? (
              <img
                src={user.profilePicture.startsWith('http') ? user.profilePicture : getCloudFrontUrl(user.profilePicture)}
                alt=""
                // A key that no longer resolves renders as an empty box, which
                // reads as "my picture vanished" — fall back to the mark.
                onError={() => setAvatarBroke(true)}
                className="w-9 h-9 rounded-full object-cover ring-2 ring-white dark:ring-slate-700"
              />
            ) : (
              <span className="w-9 h-9 rounded-full grid place-items-center bg-blue-600 text-white">
                <UserIcon size={17} />
              </span>
            )}
            <span className="hidden md:block text-left leading-tight">
              <span className="block text-[13px] font-bold text-slate-800 dark:text-slate-100 max-w-[150px] truncate">
                {user?.name || 'Admin'}
              </span>
              <span className="block text-[11px] text-slate-400">{ROLE_LABEL[user?.role] || user?.role}</span>
            </span>
            <ChevronDown size={15} className="hidden md:block text-slate-400" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-2xl shadow-2xl overflow-hidden
                            bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email || ROLE_LABEL[user?.role]}</p>
              </div>
              <button onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200
                           hover:bg-slate-50 dark:hover:bg-slate-800 transition">
                <UserIcon size={16} /> Profile
              </button>
              <button onClick={() => { if (user?.id) localStorage.setItem(`lastLogout_${user.id}`, new Date().toISOString()); logout(); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-red-600 dark:text-red-400
                           hover:bg-red-50 dark:hover:bg-red-900/20 transition border-t border-slate-100 dark:border-slate-800">
                <LogOut size={16} /> Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AdminTopbar;
